/**
 * Where each line of text sits on a rendered page, so the PDF can carry an
 * invisible copy of it on top of the page image — that's what makes the
 * PDF's text selectable, copyable and searchable.
 */
export interface TextRun {
    text: string;
    /** Position and size in CSS pixels, relative to the page's top-left. */
    x: number;
    y: number;
    width: number;
    height: number;
    fontPx: number;
}

/** Every visual line of text on the page (wrapped text gives one run per line). */
export function collectTextRuns(page: HTMLElement): TextRun[] {
    const origin = page.getBoundingClientRect();
    const runs: TextRun[] = [];
    const range = document.createRange();
    const walker = document.createTreeWalker(page, NodeFilter.SHOW_TEXT);

    for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
        const raw = node.data;
        const parent = node.parentElement;
        if (!raw.trim() || !parent || parent.closest("style")) continue;
        const style = getComputedStyle(parent);
        const fontPx = parseFloat(style.fontSize) || 12;
        const upper = style.textTransform === "uppercase";

        // The first and last characters drawn on the current line. Spaces
        // the browser collapses take no room and are left out; a drawn space
        // at either end is kept, so "13 pcs" + " across" copies with its space.
        let first = -1;
        let last = -1;
        let box: { left: number; top: number; right: number; bottom: number } | null = null;
        const flush = () => {
            const text = first < 0 ? "" : raw.slice(first, last + 1).replace(/\s+/g, " ");
            if (box && text.trim()) {
                runs.push({
                    text: upper ? text.toUpperCase() : text,
                    x: box.left - origin.left,
                    y: box.top - origin.top,
                    width: box.right - box.left,
                    height: box.bottom - box.top,
                    fontPx,
                });
            }
        };

        for (let i = 0; i < raw.length; i++) {
            range.setStart(node, i);
            range.setEnd(node, i + 1);
            const r = range.getBoundingClientRect();
            if (r.width === 0 && r.height === 0) continue;
            if (box && Math.abs(r.top - box.top) > fontPx * 0.5) {
                // Wrapped onto a new line.
                flush();
                first = -1;
                box = null;
            }
            if (first < 0) first = i;
            last = i;
            box = box
                ? { left: Math.min(box.left, r.left), top: Math.min(box.top, r.top), right: Math.max(box.right, r.right), bottom: Math.max(box.bottom, r.bottom) }
                : { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
        }
        flush();
    }
    return runs;
}
